import { defineComponent as _defineComponent } from 'vue'
import {
	ObjectType,
	Platform,
	SortOrder,
	SportCompetitionV2,
	SportEventAggregationKeyV2,
	SportEventContentV2,
	SportEventStatus,
	SportType,
} from '@/@types/graphql-types';
import AdUnit from '@/components/AdUnit.vue';
import Breadcrumbs from '@/features/sports/components/ui/Breadcrumbs.vue';
import CompetitionFixtures from '@/features/sports/components/competition/CompetitionFixtures.vue';
import CompetitionLogo from '@/features/sports/components/ui/CompetitionLogo.vue';
import Header from '@/features/sports/components/ui/Header.vue';
import ProviderStreamsComparator from '@/features/sports/components/global/ProviderStreamsComparator.vue';
import SingleStepEventEncounterList from '@/features/sports/components/global/SingleStepEventEncounterList.vue';
import SportsLayout from '@/features/sports/components/global/SportsLayout.vue';
import SportSpinningText from '@/features/sports/components/spinning-texts/SportSpinningText.vue';
import {
	getCompetitionSportSpinningText,
	SportCompetitionSpinningTextOptions,
} from '@/features/sports/components/spinning-texts';
import { quadrennialComps } from '@/features/sports/constants';
import { CollectionType } from '@/enums/collection-type';
import { isMultiStepSport } from '@/features/sports/types/enums';
import { useQuery } from '@/helpers/composables/useApollo';
import { useI18n } from '@/helpers/composables/useI18n';
import {
	useFilterStore,
	useLanguageStore,
	useMetaStore,
	useRoutingStore,
	useUserStore,
} from '@/helpers/composables/useStores';
import { shiftDateWithTimezone } from '@/helpers/date-helper';
import { captureMessageForSentry } from '@/helpers/sentry-helper';
import type { SingleStepCompetitionContext } from '@/features/sports/helpers/config';
import { getSportPageContext } from '@/features/sports/helpers/config';
import { SnowplowSportContext } from '@/helpers/tracking/providers';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { BreadcrumbItem, SportPageType } from '@/features/sports/types/interfaces';
import { CompetitionStages } from '@/features/sports/types/competition-stages';
import {
	GetCompetitionPageV2Document,
	GetCompetitionPageV2Query,
	GetCompetitionPageV2QueryVariables,
} from '@/features/sports/graphql/queries/GetCompetitionPageV2.query';
import { competitionPageTexts } from '@/pages/sports/competition-page-texts-config';
import { computed, onActivated, ref } from 'vue';
import { Route } from 'vue-router';

import { useSportEvents } from '@/features/sports/composables/useSportEvents';


export default /*#__PURE__*/_defineComponent({
  __name: 'SingleStepCompetition',
  props: {
    lockedRoute: null,
    competitionId: null
  },
  setup(__props: any) {

const props = __props as {
	lockedRoute: Route;
	competitionId?: number;
};

const { t, tc } = useI18n();
const languageStore = useLanguageStore();
const filterStore = useFilterStore();
const routingStore = useRoutingStore();
const userStore = useUserStore();
const metaStore = useMetaStore();

const isLoadingMore = ref(true);



const context = computed<SingleStepCompetitionContext>(() => {
	const { sport } = props.lockedRoute.meta || {};

	if (sport && !isMultiStepSport(sport)) {
		return getSportPageContext({
			sport: props.lockedRoute.meta!.sport as SportType,
			pageType: SportPageType.SportCompetition,
		});
	}

	return {} as SingleStepCompetitionContext;
});

const queryFrom = computed(() => shiftDateWithTimezone(languageStore.localeObject.value.timezone));
const queryVariables = computed<GetCompetitionPageV2QueryVariables>(() => {
	return {
		language: languageStore.language.value,
		country: languageStore.country.value,
		fullPath: props.lockedRoute.path,
		site: routingStore.activeDomain.value,
		platform: Platform.Web,
		first: 20,
		filter: {
			startAt: { from: queryFrom.value.toISOString() },
			packages: filterStore.filters.value(CollectionType.SPORTS)?.providers ?? [],
			sports: [context.value.sportType],
		},
		timezoneOffset: 0,
	};
});

const queriesEnabled = computed(() => !!(props.lockedRoute.name && props.lockedRoute.name.includes('competition')));

const competitionQuery = useQuery<GetCompetitionPageV2Query, GetCompetitionPageV2QueryVariables>(
	GetCompetitionPageV2Document,
	queryVariables,
	{
		fetchPolicy: 'cache-and-network',
		enabled: queriesEnabled,
	}
);
competitionQuery.onResult(({ data }) => updateJsonLd(data?.urlV2.node as SportCompetitionV2));

const pastCompetitionFrom = computed(() => {
	// limit querying back 4+ years ago to competitions with a 4-year cycle, to reduce the amount of data being processed on BE

	return props.competitionId && quadrennialComps.includes(props.competitionId)
		? new Date('2021-01-01T00:00:00.000Z')
		: new Date('2023-01-01T00:00:00.000Z');
});

const pastCompetitionQuery = useQuery<GetCompetitionPageV2Query, GetCompetitionPageV2QueryVariables>(
	GetCompetitionPageV2Document,
	() => ({
		...queryVariables.value,
		filter: {
			...queryVariables.value.filter,
			startAt: {
				...queryVariables.value.filter?.startAt,
				to: queryFrom.value.toISOString(),
				from: pastCompetitionFrom.value,
			},
		},
		sortOrder: SortOrder.Desc,
	}),
	{
		fetchPolicy: 'cache-and-network',
		enabled: queriesEnabled,
	}
);

pastCompetitionQuery.onResult(({ data }) => updateJsonLd(data?.urlV2.node as SportCompetitionV2));

const isDataLoading = computed(() => !!competitionQuery.loading.value && !!pastCompetitionQuery.loading.value);

const hasUpcomingEvents = computed(() => {
	const node = competitionQuery.result.value?.urlV2.node as SportCompetitionV2;
	if ((node?.sportEventsV2.edges?.length || 0) === 0) {
		return false;
	}

	// we assume there are upcoming events before any requests have been done
	return true;
});

const competitionData = computed<SportCompetitionV2 | undefined>(() => {
	if (isDataLoading.value) {
		return undefined;
	}

	return hasUpcomingEvents.value
		? (competitionQuery.result.value?.urlV2.node as SportCompetitionV2)
		: (pastCompetitionQuery.result.value?.urlV2.node as SportCompetitionV2);
});

const breadcrumbs = computed<BreadcrumbItem[]>(() => {
	// @todo remove when there are no duplicate event items that cause `get breadcrumbs` to stumble and break the whole page
	if (competitionData.value?.content == null) return [];

	return [
		// e.g. Sport
		{ name: tc('WEBAPP_SPORTS'), routeName: 'app.sports.subdomain.all' },

		// e.g. Fussball
		{
			name: tc(context.value.sportName),
			objectType: SportPageType.SportOverview,
			url: context.value?.getOverviewUrl(languageStore.webLocale.value),
		},

		// e.g. Bundesliga
		{
			name: competitionData.value?.content.name,
			objectType: ObjectType.SportCompetition,
			objectId: competitionData.value?.objectId,
			url: competitionData.value?.content.fullPath,
		},
	];
});

const schemaEvents = computed(() => {
	const events = competitionData.value?.sportEventsV2?.edges || [];

	return events.map((event: any) => {
		const eventData = event.node;

		const { key, params } = context.value.getEventTitle(eventData, languageStore.webLocale.value);
		const eventTitle = t(key, params);

		const prices =
			eventData?.offers?.map((offer: any) => offer.retailPrice as number).filter((v: any) => v != null) ?? [];

		let eventStatus = '';
		switch (eventData.content?.status) {
			case SportEventStatus.Postponed:
				eventStatus = 'EventPostponed';
				break;
			case SportEventStatus.Cancelled:
				eventStatus = 'EventCancelled';
				break;
			default:
				eventStatus = 'EventScheduled';
		}

		return {
			'@context': 'https://schema.org',
			'@type': 'BroadcastEvent',
			name: `${eventTitle} ${competitionData.value?.content.name} ${
				eventData.competition?.activeSeason?.name ?? ''
			}`,
			isLiveBroadcast: (eventData?.offers ?? []).length > 0,
			startDate: eventData.content?.startAt ?? '',
			endDate: eventData.content?.endAt ?? '',
			broadcastOfEvent: {
				'@type': 'SportsEvent',
				sport: context.value.sportType.toLowerCase().replace(/_/g, ' '),
				eventStatus: `https://schema.org/${eventStatus}`,
				name: `${eventTitle} ${competitionData.value?.content.name} ${
					eventData.competition?.activeSeason?.name ?? ''
				}`,
				awayTeam: {
					'@type': 'SportsTeam',
					name:
						eventData?.competitors?.awayTeam?.competitors.map((c: any) => c.content.name).find(Boolean) ??
						'',
					sport: context.value.sportType.toLowerCase().replace(/_/g, ' '),
				},
				homeTeam: {
					'@type': 'SportsTeam',
					name:
						eventData?.competitors?.homeTeam?.competitors.map((c: any) => c.content.name).find(Boolean) ??
						'',
					sport: context.value.sportType.toLowerCase().replace(/_/g, ' '),
				},
				startDate: eventData.content?.startAt ?? '',
				endDate: eventData.content?.endAt ?? '',
				location: {
					'@type': 'Place',
					name: eventData.content?.venue?.name,
				},
				offers: eventData?.offers
					? {
							'@type': 'AggregateOffer',
							highPrice: Math.max(...prices) ?? undefined,
							lowPrice: Math.min(...prices) ?? undefined,
							priceCurrency: eventData?.offers.map((offer: any) => offer.currency).find(Boolean),
							offerCount: eventData?.offers.length,
							offers: eventData?.offers.map((offer: any) => ({
								'@type': 'Offer',
								name: `${eventTitle} ${offer.presentationType} ${offer.monetizationType}`,
								price: offer.retailPrice,
								priceCurrency: offer.currency,
								url: offer.standardWebURL,
							})),
					  }
					: null,
				audience: {
					'@type': 'Audience',
					name: 'Sports Fans',
				},
			},
		};
	});
});

const schemaData = computed(() => {
	const schemaDataObject = {
		'@context': 'http://schema.org',
		'@type': 'EventSeries',
		name: competitionData.value?.content.name,
		subEvents: schemaEvents.value,
	};
	return JSON.parse(JSON.stringify(schemaDataObject).replace(/&quot;/g, '"')) || '';
});

const translatedCountry = computed(() => {
	if (languageStore.country.value === 'US') return t('WEBAPP_COUNTRY_THE_US');
	if (languageStore.country.value === 'GB') return t('WEBAPP_COUNTRY_THE_GB');

	return t('WEBAPP_COUNTRY_' + languageStore.country.value);
});

// Extract events from the already-fetched competition query data to prevent query waterfall and CLS
const providedEventsFromQuery = computed(() => {
	// Try upcoming query first (most common case)
	const upcomingEvents = extractEventsFromCompetition(
		competitionQuery.result.value?.urlV2.node as SportCompetitionV2
	);
	if (upcomingEvents.length > 0) {
		return upcomingEvents;
	}

	// Fall back to past events query if upcoming has no events
	const pastEvents = extractEventsFromCompetition(
		pastCompetitionQuery.result.value?.urlV2.node as SportCompetitionV2
	);
	if (pastEvents.length > 0) {
		return pastEvents;
	}

	return null;
});

const { sportEvents, events } = useSportEvents({
	context,
	competitionId: () => competitionData.value?.id ?? null,
	competitionObjectId: () => competitionData.value?.objectId ?? null,
	allowPastEvents: () => !hasUpcomingEvents.value,
	providedEvents: providedEventsFromQuery,
});

const competitionText = computed(() => {
	const competitionId = competitionData.value?.id;
	if (competitionId == null) return '';

	// @ts-ignore
	return competitionPageTexts?.[competitionId]?.[languageStore.country.value] ?? '';
});

function updateJsonLd(node: SportCompetitionV2) {
	if (node?.sportEventsV2?.edges?.length) {
		metaStore.setJsonLd(schemaData.value);
	}
}

function getSpinningText(options: SportCompetitionSpinningTextOptions) {
	return getCompetitionSportSpinningText(options);
}

// TRACKING
const trackingContexts = computed<SnowplowSportContext[]>(() => {
	const sportContext = new SnowplowSportContext(
		props.lockedRoute.meta!.sport as SportType,
		SportPageType.SportCompetition
	);
	sportContext.competitionId = competitionData.value?.objectId;
	sportContext.seasonId = parseInt(competitionData.value?.activeSeason?.name || '');

	return sportContext !== null ? [sportContext] : [];
});

onActivated(() => {
	// note: all sport pages are excluded from automatic page tracking
	/**
	 * setTimeout added here to prevent pageView to be triggered before link click track event.
	 * that's because when adding the @click.native to router-link the track event will get triggered
	 * after the navigation which causes the pageView to get triggered first.
	 *
	 */
	setTimeout(() => {
		TrackingHelper.trackPage(props.lockedRoute.path, trackingContexts.value);
	});
});

const competitionStages = computed<CompetitionStages[]>(() => {
	const bucket =
		competitionData.value?.sportEventsV2?.edges?.reduce((acc: Record<string, CompetitionStages>, edge) => {
			let key = '';
			const { name, number } = edge?.node?.content?.competitionStage;
			key = name + number;

			const bucket = acc[key] ?? { competitionStage: edge?.node?.content?.competitionStage, data: [] };
			bucket.data.push(edge);
			acc[key] = bucket;

			return acc;
		}, {}) || {};
	return Object.values(bucket);
});

const isNBA = computed(() => competitionData.value?.id === 'scn-bkb-8331232');
const isNFL = computed(() => competitionData.value?.id === 'scn-afb-7440571');
const isMLB = computed(() => competitionData.value?.id === 'scn-bsb-11678542');
const isNHL = computed(() => competitionData.value?.id === 'scn-ice-17698463');

async function onLoadNext() {
	const pageInfo = competitionData.value?.sportEventsV2.pageInfo;
	isLoadingMore.value = true;

	const query = hasUpcomingEvents.value ? competitionQuery : pastCompetitionQuery;
	const competitionFetchMoreQuery = query.fetchMore({
		variables: { after: pageInfo?.endCursor },
		updateQuery(previousResult, { fetchMoreResult }) {
			const node = previousResult.urlV2.node as SportCompetitionV2;
			const fetchMoreNode = fetchMoreResult?.urlV2.node as SportCompetitionV2;
			node.sportEventsV2.pageInfo = fetchMoreNode.sportEventsV2.pageInfo;

			node.sportEventsV2.edges = [
				...(node.sportEventsV2.edges || []),
				...(fetchMoreNode.sportEventsV2.edges || []),
			];

			return previousResult;
		},
	});

	if (competitionFetchMoreQuery == null) return;

	competitionFetchMoreQuery
		.catch(e => {
			captureMessageForSentry(
				'[GraphQL Competition Page Load More Buckets Issue]:',
				{ error: e, where: 'Component: CompetitionFixtures, method: onLoadNext' },
				'error'
			);
		})
		.finally(() => {
			isLoadingMore.value = false;
		});
}

function extractEventsFromCompetition(competition: SportCompetitionV2 | undefined) {
	return (competition?.sportEventsV2?.edges ?? []).map(edge => edge.node);
}

function getStage(key: SportEventAggregationKeyV2) {
	const now = new Date().getTime();

	if (isNBA.value) {
		if (now <= new Date('2024-04-14').setUTCHours(23, 59, 59, 999)) return tc('SPORTS_WEBAPP_REGULAR_SEASON');
		return t('SPORTS_WEBAPP_GAMES');
	}

	if (isNFL.value) {
		if (
			now <= new Date('2024-01-12').setUTCHours(23, 59, 59, 999) &&
			![
				'AMERICAN_FOOTBALL_CONFERENCE_CHAMPIONSHIP',
				'AMERICAN_FOOTBALL_CONFERENCE_DIVISIONAL_ROUND',
				'AMERICAN_FOOTBALL_CONFERENCE_WILD_CARD_ROUND',
				'NATIONAL_FOOTBALL_CONFERENCE_CHAMPIONSHIP',
				'NATIONAL_FOOTBALL_CONFERENCE_DIVISIONAL_ROUND',
				'NATIONAL_FOOTBALL_CONFERENCE_WILD_CARD_ROUND',
			].includes(key.competitionStage?.name as string)
		)
			return tc('SPORTS_WEBAPP_REGULAR_SEASON');
	}

	if (isMLB.value) {
		if (now <= new Date('2023-10-01').setUTCHours(23, 59, 59, 999)) return tc('SPORTS_WEBAPP_REGULAR_SEASON');
	}

	if (isNHL.value) {
		if (now <= new Date('2024-04-18').setUTCHours(23, 59, 59, 999)) return tc('SPORTS_WEBAPP_REGULAR_SEASON');
	}

	return context.value?.getStageHeading(key.competitionStage!, t);
}

function getStageFromEvent(event: SportEventContentV2) {
	return getStage({ competitionStage: event.competitionStage });
}

return { __sfc: true,t, tc, languageStore, filterStore, routingStore, userStore, metaStore, isLoadingMore, props, context, queryFrom, queryVariables, queriesEnabled, competitionQuery, pastCompetitionFrom, pastCompetitionQuery, isDataLoading, hasUpcomingEvents, competitionData, breadcrumbs, schemaEvents, schemaData, translatedCountry, providedEventsFromQuery, sportEvents, events, competitionText, updateJsonLd, getSpinningText, trackingContexts, competitionStages, isNBA, isNFL, isMLB, isNHL, onLoadNext, extractEventsFromCompetition, getStage, getStageFromEvent, AdUnit, Breadcrumbs, CompetitionFixtures, CompetitionLogo, Header, ProviderStreamsComparator, SingleStepEventEncounterList, SportsLayout, SportSpinningText }
}

})